const PGAdapter = require('./../../common/pg-adapter')
const omitBy = require('lodash/omitBy')
const isNil = require('lodash/isNil')
const { SHIPMENT_STATUS } = require('../constants')
const { buildOtp } = require('../utils/utils')

const DATA_SHIPMENTSNAPSHOT_TABLE_NAME = 'avocado.data_shipmentsnapshot'
const DATA_SHIPMENTSNAPSHOT_COLUMNS = [
  'id',
  'shipment_id',
  'shipment_number',
  'status',
  'route_id',
  'funder_id',
  'program_id',
  'vendor_id',
  'origin_id',
  'destination_id',
  'notes',
  'is_automatic_return',
  'parent_doc_id',
  'order_id',
  'created_at',
  'updated_at',
  'created_by',
  'couchdb_id',
  'suborder_id',
  'is_gps_authenticated',
  'gps_validation_fail_comment',
  'gps_validation',
  'raw_order_id'
]

const DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME = 'avocado.data_shipmentsnapshot_change'
const DATA_SHIPMENT_COMMENT_TABLE_NAME = 'avocado.data_shipment_comment'
const DATA_SHIPMENT_COLLECTOR_TABLE_NAME = 'avocado.data_shipment_collector'

// `getShipmentDocRows` returns its rows inside jsonb, which renders a
// `timestamptz` with a `+00:00` offset rather than the `Z` that pg's own Date
// parsing plus `toISOString()` produced. Rendering in SQL instead keeps the
// rebuilt documents carrying byte-identical strings: `to_char` in UTC with
// millisecond precision is exactly `Date.prototype.toISOString()`'s format.
// Fixed width also means ordering by the text is still chronological.
const isoUtc = column => `to_char(${column} AT TIME ZONE 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS.MS"Z"')`

// Fallback PG statement budget when the caller does not pass one (local
// runners, direct API use). The route-shipment-cache Lambda derives and passes
// its real remaining budget per invocation (see handler.ts), so changing the
// Lambda timeout changes the server-side budget automatically. The point of
// the setting: kill the producer query before the Lambda dies, so Postgres
// never keeps executing an orphaned statement after the client is gone
// (orphans were observed burning CPU for up to 20 minutes - issue 25067).
const ROUTE_CACHE_STATEMENT_TIMEOUT_MS = 110000

// work_mem for the route cache refresh transaction. The materialized snapshot
// set is read by eight aggregations. At the server default of 16 MB it does
// not fit in memory: on the production primary in September 2026 each refresh
// wrote about 150 MB of temp files and read about 600 MB back. That I/O
// competes for the same volume as the table reads of the query.
const ROUTE_CACHE_WORK_MEM = '128MB'

class ShipmentSnapshotAdapter extends PGAdapter {
  constructor (pgConnection, username, logger, options = {}) {
    super(
      pgConnection,
      DATA_SHIPMENTSNAPSHOT_TABLE_NAME,
      username,
      DATA_SHIPMENTSNAPSHOT_COLUMNS,
      'id',
      null,
      logger,
      { strictColumns: false, withGlobalAccessUser: options.withGlobalAccessUser }
    )
  }

  /**
   * Get CouchDB IDs and location IDs for shipment snapshots by route IDs
   * @param {string[]} routeIds - Array of route IDs to filter by
   * @param {object} options - Query options
   * @param {string[]} [options.programIds] - Filter by program IDs
   * @param {string} [options.cutoffDate] - ISO date string (e.g., '2024-01-01T00:00:00.000Z')
   * @returns {Promise<{couchdbIds: string[], locationIds: string[]}>} Object with couchdbIds and unique locationIds (origins + destinations)
   */
  async getCouchdbIdsByRoutes (routeIds, options = {}) {
    if (!Array.isArray(routeIds)) {
      throw new Error('routeIds must be an array')
    }
    if (routeIds.length === 0) {
      return { couchdbIds: [], locationIds: [], funderIds: [], serviceIds: [], productIds: [] }
    }

    const { programIds = [], cutoffDate = null } = options

    const params = []
    let paramIndex = 1

    let query = `
      SELECT ss.couchdb_id,
             ss.origin_id,
             ss.destination_id,
             ss.funder_id,
             s.fsid as service_id,
             ARRAY_AGG('product:' || ssl.sku) AS skus
      FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
      LEFT JOIN avocado.data_shipmentsnapshotline ssl ON ss.id = ssl.shipment_snapshot_id
      LEFT JOIN avocado.data_program p ON ss.program_id = p.fsid
      LEFT JOIN avocado.data_service s ON p.id = s.program_id
      WHERE ss.couchdb_id IS NOT NULL
        AND ss.route_id = ANY($${paramIndex++}::text[])
    `

    params.push(routeIds)

    if (programIds.length > 0) {
      query += ` AND ss.program_id = ANY($${paramIndex++}::text[])`
      params.push(programIds)
    }

    if (cutoffDate) {
      query += ` AND ss.updated_at >= $${paramIndex++}`
      params.push(cutoffDate)
    }

    // We add the Group by clause after adding potential filters
    query += ` GROUP BY ss.couchdb_id, ss.origin_id, ss.destination_id, ss.funder_id, s.fsid`

    try {
      const result = await this.pgConnection.query(query, params)

      const couchdbIds = result.rows.map(row => row.couchdb_id)
      // Dedupe location IDs because multiple shipments may share the same origin/destination.
      // These IDs are used for offline locationMatch filtering in the van-shipments dispenser.
      const locationSet = new Set()
      const skuSet = []
      const funderSet = new Set()
      const serviceSet = new Set()

      result.rows.forEach(row => {
        if (row.origin_id) locationSet.add(row.origin_id)
        if (row.destination_id) locationSet.add(row.destination_id)
        if (row.skus && Array.isArray(row.skus)) {
          skuSet.push(...row.skus)
        }
        if (row.funder_id) funderSet.add(row.funder_id)
        if (row.service_id) serviceSet.add(row.service_id)
      })

      return {
        couchdbIds,
        locationIds: Array.from(locationSet),
        funderIds: Array.from(funderSet),
        serviceIds: Array.from(serviceSet),
        productIds: [...new Set(skuSet)]
      }
    } catch (error) {
      if (this.logger) {
        this.logger.error({
          code: 'shipment-snapshot-query-failed',
          routeIds,
          programIds,
          cutoffDate,
          error: error.message
        })
      }
      throw error
    }
  }

  /**
   * Get CouchDB IDs, location IDs, funder IDs, service IDs, product IDs, and change doc IDs
   * for shipment snapshots grouped by route ID.
   * Aggregates per route on the server and returns one row per route.
   *
   * The whole refresh runs on one pinned client in a single transaction so the
   * settings below stay scoped to this refresh:
   *  - SET LOCAL statement_timeout kills the query before the Lambda's own
   *    timeout fires, so a killed invocation never leaves an orphaned
   *    statement burning server CPU (observed up to 20 minutes - issue 25067).
   *    The budget derives from the Lambda's remaining time per invocation.
   *    Overlap between scheduled runs is prevented upstream instead: the
   *    Lambda is limited to one concurrent execution and a failed or delayed
   *    invocation is not retried.
   *  - SET LOCAL work_mem keeps the materialized snapshot set and the hash
   *    aggregations in memory (see ROUTE_CACHE_WORK_MEM).
   *
   * Aggregation formulation notes (production-measured, July 2026):
   *  - services: dedupe (route_id, program_id) BEFORE joining data_program/
   *    data_service. Joining at snapshot granularity fanned out to 878,226
   *    rows (planner est. 8,075) and sorted 82.8 MB on disk; the pre-dedup
   *    form measured 2,720 ms -> 76 ms.
   *  - id arrays use SELECT DISTINCT + array_agg (hash dedup) instead of
   *    ARRAY_AGG(DISTINCT ...) (Sort + GroupAggregate, spills to disk).
   *  - couchdb_ids drops DISTINCT: couchdb_id is unique per snapshot row.
   *
   * Products and batches (production-measured, September 2026):
   *  - line_facts joins the snapshot, line and line-details base tables once
   *    and repeats the snapshot filter instead of reading the snapshots CTE.
   *    Postgres cannot scan a CTE in a parallel worker, so a join on the CTE
   *    ran all index probes (about 106k for lines, 380k for details) in one
   *    process, and the products and batches CTEs each probed the lines.
   *    The base-table join reads each line once and can run in parallel
   *    workers. When the pages are not in memory, each worker reads its own
   *    pages, so the disk reads run in parallel too.
   *  - With all pages in memory the refresh measured 9.2 s -> 5.0 s on the
   *    primary (4 workers).
   *  - line_facts reads ssl.id for the details join. This keeps the planner
   *    away from an Index Only Scan on the "unique shipment_snapshot_id:sku"
   *    index, which measured 0.28-0.32 ms per probe in July 2026 (0.005 ms
   *    via the plain index).
   * @param {object} options - Query options
   * @param {string} [options.cutoffDate] - ISO date string - only return snapshots updated after this date
   * @param {number} [options.statementTimeoutMs] - PG statement_timeout for the refresh, in ms. The Lambda handler derives this from its own remaining budget; defaults to ROUTE_CACHE_STATEMENT_TIMEOUT_MS
   * @returns {Promise<Object<string, {carrierId: string|null, couchdbIds: string[], locationIds: string[], funderIds: string[], serviceIds: string[], productIds: string[], changeDocIds: string[]}>>} Map of routeId -> route data
   */
  async getCouchdbIdsByRoutesGrouped (options = {}) {
    const { cutoffDate = null, statementTimeoutMs = ROUTE_CACHE_STATEMENT_TIMEOUT_MS } = options

    // Production hands us a pg Pool to pin a client from; test harnesses may
    // pass an already-connected Client instead. Pool-only getters (idleCount,
    // totalCount) tell the two apart without instanceof. The pin happens
    // inside the try so a connect failure lands in the same logged error path
    // as query failures.
    const isPool = typeof this.pgConnection.idleCount === 'number'
    let client
    try {
      client = isPool ? await this.pgConnection.connect() : this.pgConnection
      await client.query('BEGIN')

      await client.query(`SET LOCAL statement_timeout = ${statementTimeoutMs}`)
      await client.query(`SET LOCAL work_mem = '${ROUTE_CACHE_WORK_MEM}'`)

      // Keep a snapshot in the window if EITHER it OR its parent route was
      // touched recently. `assignCarrier` (and other route-only mutations)
      // bumps `data_route.updated_at` but not the snapshots', so checking
      // only snapshots would silently drop a route's shipments after its
      // snapshots aged out even though the route itself is current.
      // Fetching the recent route ids first (data_route is tiny) keeps the
      // main filter's OR on a single table, where indexes can serve it.
      let params = []
      let cutoffClause = ''
      if (cutoffDate) {
        const recentRoutes = await client.query(
          'SELECT couchdb_id FROM avocado.data_route WHERE updated_at >= $1',
          [cutoffDate]
        )
        params = [cutoffDate, recentRoutes.rows.map(row => row.couchdb_id)]
        cutoffClause = `
            AND (ss.updated_at >= $1 OR ss.route_id = ANY($2::text[]))`
      }

      const snapshotFilter = `ss.route_id IS NOT NULL
            AND ss.couchdb_id IS NOT NULL${cutoffClause}`

      // The filtered snapshot set is materialized once and shared by the
      // snapshot-level aggregations. line_facts applies the same filter to
      // the base table again (see the notes above). Both reads run in one
      // statement and so use one database snapshot: they see the same rows.
      // Each follow-up CTE groups per route; the line/details fan-out stays
      // inside line_facts and never multiplies the snapshot-level rows.
      const query = `
        WITH snapshots AS MATERIALIZED (
          SELECT ss.route_id, ss.couchdb_id, ss.shipment_id,
                 ss.origin_id, ss.destination_id, ss.funder_id,
                 ss.program_id, ss.updated_at, r.carrier_id
          FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
          LEFT JOIN avocado.data_route r ON ss.route_id = r.couchdb_id
          WHERE ${snapshotFilter}
        ),
        routes AS (
          SELECT route_id,
                 MAX(carrier_id::text) AS carrier_id,
                 MAX(updated_at) AS last_updated
          FROM snapshots
          GROUP BY route_id
        ),
        couchdb AS (
          SELECT route_id, ARRAY_AGG(couchdb_id) AS couchdb_ids
          FROM snapshots
          GROUP BY route_id
        ),
        origins AS (
          SELECT route_id, ARRAY_AGG(origin_id) AS origin_ids
          FROM (SELECT DISTINCT route_id, origin_id FROM snapshots WHERE origin_id IS NOT NULL) d
          GROUP BY route_id
        ),
        destinations AS (
          SELECT route_id, ARRAY_AGG(destination_id) AS destination_ids
          FROM (SELECT DISTINCT route_id, destination_id FROM snapshots WHERE destination_id IS NOT NULL) d
          GROUP BY route_id
        ),
        funders AS (
          SELECT route_id, ARRAY_AGG(funder_id) AS funder_ids
          FROM (SELECT DISTINCT route_id, funder_id FROM snapshots WHERE funder_id IS NOT NULL) d
          GROUP BY route_id
        ),
        services AS (
          SELECT route_id, ARRAY_AGG(fsid) AS service_ids
          FROM (
            SELECT DISTINCT rp.route_id, sv.fsid
            FROM (SELECT DISTINCT route_id, program_id FROM snapshots WHERE program_id IS NOT NULL) rp
            JOIN avocado.data_program p ON rp.program_id = p.fsid
            JOIN avocado.data_service sv ON p.id = sv.program_id
            WHERE sv.fsid IS NOT NULL
          ) d
          GROUP BY route_id
        ),
        line_facts AS MATERIALIZED (
          SELECT DISTINCT ss.route_id, ssl.sku, ssld.batch
          FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
          JOIN avocado.data_shipmentsnapshotline ssl ON ssl.shipment_snapshot_id = ss.id
          LEFT JOIN avocado.data_shipmentsnapshotline_details ssld ON ssld.shipmentsnapshot_line = ssl.id
          WHERE ${snapshotFilter}
        ),
        products AS (
          SELECT route_id, ARRAY_AGG(product) AS product_ids
          FROM (
            SELECT DISTINCT route_id, 'product:' || sku AS product
            FROM line_facts
            WHERE sku IS NOT NULL
          ) d
          GROUP BY route_id
        ),
        batches AS (
          SELECT route_id, ARRAY_AGG(batch) AS batch_ids
          FROM (
            SELECT DISTINCT route_id, batch
            FROM line_facts
            WHERE batch IS NOT NULL
          ) d
          GROUP BY route_id
        ),
        changes AS (
          SELECT route_id, ARRAY_AGG(couchdb_id) AS change_doc_ids
          FROM (
            SELECT DISTINCT s.route_id, sc.couchdb_id
            FROM snapshots s
            JOIN ${DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME} sc ON sc.snapshot_couchdb_id = s.couchdb_id
            WHERE sc.couchdb_id IS NOT NULL
          ) d
          GROUP BY route_id
        ),
        shipments AS (
          SELECT DISTINCT route_id, shipment_id
          FROM snapshots
          WHERE shipment_id IS NOT NULL
        ),
        otps AS (
          SELECT route_id, ARRAY_AGG(couchdb_id) AS otp_doc_ids
          FROM (
            SELECT DISTINCT sh.route_id, so.couchdb_id
            FROM shipments sh
            JOIN avocado.shipment_otp so ON so.shipment_id = sh.shipment_id
            WHERE so.couchdb_id IS NOT NULL
          ) d
          GROUP BY route_id
        ),
        comments AS (
          SELECT route_id, ARRAY_AGG(couchdb_id) AS comment_doc_ids
          FROM (
            SELECT DISTINCT sh.route_id, sc.couchdb_id
            FROM shipments sh
            JOIN ${DATA_SHIPMENT_COMMENT_TABLE_NAME} sc ON sc.shipment_id = sh.shipment_id
            WHERE sc.couchdb_id IS NOT NULL
          ) d
          GROUP BY route_id
        )
        SELECT r.route_id,
               r.carrier_id,
               r.last_updated,
               c.couchdb_ids,
               o.origin_ids,
               d.destination_ids,
               f.funder_ids,
               sv.service_ids,
               p.product_ids,
               b.batch_ids,
               ch.change_doc_ids,
               ot.otp_doc_ids,
               cm.comment_doc_ids
        FROM routes r
        LEFT JOIN couchdb c USING (route_id)
        LEFT JOIN origins o USING (route_id)
        LEFT JOIN destinations d USING (route_id)
        LEFT JOIN funders f USING (route_id)
        LEFT JOIN services sv USING (route_id)
        LEFT JOIN products p USING (route_id)
        LEFT JOIN batches b USING (route_id)
        LEFT JOIN changes ch USING (route_id)
        LEFT JOIN otps ot USING (route_id)
        LEFT JOIN comments cm USING (route_id)`

      const result = await client.query(query, params)

      const routeMap = {}
      for (const row of result.rows) {
        routeMap[row.route_id] = {
          carrierId: row.carrier_id || null,
          couchdbIds: row.couchdb_ids || [],
          commentDocIds: row.comment_doc_ids || [],
          locationIds: Array.from(new Set([
            ...(row.origin_ids || []),
            ...(row.destination_ids || [])
          ])),
          funderIds: row.funder_ids || [],
          serviceIds: row.service_ids || [],
          productIds: row.product_ids || [],
          changeDocIds: row.change_doc_ids || [],
          otpDocIds: row.otp_doc_ids || [],
          lastUpdated: row.last_updated,
          batchIds: row.batch_ids || []
        }
      }

      await client.query('COMMIT')
      return routeMap
    } catch (error) {
      if (client) await client.query('ROLLBACK').catch(() => {})
      if (this.logger) {
        this.logger.error({
          code: 'shipment-snapshot-grouped-query-failed',
          cutoffDate,
          error: error.message
        })
      }
      throw error
    } finally {
      if (isPool && client) client.release()
    }
  }

  /**
   * Get LMD snapshot enrichment data from PostgreSQL.
   *
   * @param {string[]} shipmentIds - Array of shipment_id values
   * @returns {Promise<Object[]>} Array of enrichment objects keyed by shipment_id
   */
  async getLmdSnapshotData (shipmentIds) {
    if (!shipmentIds.length) return []

    const snapshotQuery = `
      SELECT
        ss.shipment_id,
        ss.couchdb_id,
        ss.status,
        ss.created_at,
        ss.created_by,
        ss.parent_doc_id,
        ss.raw_order_id,
        ss.is_gps_authenticated,
        ss.gps_validation_fail_comment,
        ss.gps_validation,
        (
          SELECT jsonb_object_agg(
            ssld.batch,
            jsonb_build_object('quantity', ssld.amount)
          )
          FROM avocado.data_shipmentsnapshotline ssl
          JOIN avocado.data_shipmentsnapshotline_details ssld ON ssld.shipmentsnapshot_line = ssl.id
          WHERE ssl.shipment_snapshot_id = ss.id
        ) AS counts
      FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
      WHERE ss.shipment_id = ANY($1)
      ORDER BY ss.shipment_id, ss.created_at ASC
    `

    const otpQuery = `
      SELECT
        so.shipment_id,
        so.password,
        so.otp_key,
        so.user_fail_reason,
        so.verified
      FROM avocado.shipment_otp so
      WHERE so.shipment_id = ANY($1)
    `

    // One row per shipment: the latest collector, so a shipment with collector
    // docs on several hand-off snapshots reads the most recent identity.
    const collectorQuery = `
      SELECT DISTINCT ON (shipment_id)
        shipment_id,
        collector_type,
        name,
        phone_number
      FROM ${DATA_SHIPMENT_COLLECTOR_TABLE_NAME}
      WHERE shipment_id = ANY($1)
      ORDER BY shipment_id, created_at DESC, couchdb_id DESC
    `

    const [snapshotResult, otpResult, collectorResult] = await Promise.all([
      this.pgConnection.query(snapshotQuery, [shipmentIds]),
      this.pgConnection.query(otpQuery, [shipmentIds]),
      this.pgConnection.query(collectorQuery, [shipmentIds])
    ])

    const otpByShipment = {}
    for (const row of otpResult.rows) {
      otpByShipment[row.shipment_id] = row
    }

    const collectorByShipment = {}
    for (const row of collectorResult.rows) {
      collectorByShipment[row.shipment_id] = {
        name: row.name,
        phoneNumber: row.phone_number,
        type: row.collector_type
      }
    }

    const shipmentMap = {}
    for (const row of snapshotResult.rows) {
      const entry = shipmentMap[row.shipment_id] || { snapshotDates: {}, history: {} }
      shipmentMap[row.shipment_id] = entry

      const createdAtISO = row.created_at.toISOString()

      // snapshotDates: earliest date wins per status
      if (!entry.snapshotDates[row.status] || createdAtISO < entry.snapshotDates[row.status]) {
        entry.snapshotDates[row.status] = createdAtISO
      }

      // history: keyed by couchdb_id
      if (row.couchdb_id) {
        entry.history[row.couchdb_id] = omitBy({
          createdAt: createdAtISO,
          createdBy: { user: row.created_by, agent: '' },
          counts: row.counts || {},
          parentDocId: row.parent_doc_id
        }, isNil)
      }

      entry.orderId = row.raw_order_id
      entry.isGpsAuthenticated = row.is_gps_authenticated
      entry.gpsValidationFailComment = row.gps_validation_fail_comment
      entry.gpsValidation = row.gps_validation

      if (row.status === SHIPMENT_STATUS.RECEIVED) {
        entry.driverName = row.created_by
      }
    }

    return shipmentIds.map(shipmentId => {
      const entry = shipmentMap[shipmentId]
      if (!entry) return null

      const otpRow = otpByShipment[shipmentId]
      if (otpRow) {
        entry.otp = buildOtp(otpRow)
      }

      const collector = collectorByShipment[shipmentId]
      if (collector) {
        entry.collector = collector
      }

      return { id: shipmentId, ...entry }
    }).filter(Boolean)
  }

  /**
   * Get CouchDB IDs for change documents linked to the specified snapshots
   * @param {string[]} snapshotCouchdbIds - Array of snapshot CouchDB IDs
   * @returns {Promise<string[]>} Array of change document CouchDB IDs
   */
  async getChangeDocIds (snapshotCouchdbIds) {
    if (!Array.isArray(snapshotCouchdbIds) || snapshotCouchdbIds.length === 0) {
      return []
    }

    const query = `
      SELECT sc.couchdb_id
      FROM ${DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME} sc
      WHERE sc.snapshot_couchdb_id = ANY($1::text[])
    `

    try {
      const result = await this.pgConnection.query(query, [snapshotCouchdbIds])
      return result.rows.map(row => row.couchdb_id)
    } catch (error) {
      if (this.logger) {
        this.logger.error({
          code: 'shipment-change-query-failed',
          snapshotCouchdbIds,
          error: error.message
        })
      }
      throw error
    }
  }

  /**
   * Every Postgres row that makes up one shipment, in the shape the couch
   * document set has: snapshots (with batch-level counts), change documents
   * (with their lines and vector clocks), comments and the OTP row.
   *
   * Deliberately raw — `pgRowsToShipmentDocs` turns these into couch-shaped
   * documents and `docsToShipmentRecords` does the merging, so winner
   * selection and the vector-clock ordering stay in one implementation
   * rather than being reimplemented in SQL.
   *
   * One statement, one pool connection. The four sets are tagged with a `kind`
   * and stacked with UNION ALL rather than joined together: only `changes`
   * hangs off `snapshots`, while `comments` and `otp` are keyed on
   * `shipment_id` independently. Joining siblings is a cross product, so a
   * single joined query would return (changes per snapshot x comments) rows and
   * repeat each snapshot's `counts` map and each comment's inline base64 ePOD
   * signature in every duplicated row. Those joins would also all have to be
   * LEFT: a shipment with no comments is normal, and an inner join would return
   * no rows for it, which `findFromPg` reports as empty and the controller turns
   * into a 404 on a shipment that exists. Stacking keeps the row count at
   * snapshots + changes + comments + 1 with nothing duplicated.
   *
   * @param {string} shipmentId - the couch shipment id (prefix before `:status:`)
   * @returns {Promise<{snapshots: object[], changes: object[], comments: object[], otp: object|null}>}
   */
  async getShipmentDocRows (shipmentId) {
    // Change lines are aggregated into the `changes` map shape the couch change
    // document uses, so the mapper stays a straight field rename.
    //
    // Changes are scoped by joining the snapshot row's `shipment_id` rather than
    // by prefix matching `snapshot_couchdb_id`: shipment ids are not prefix-free
    // (a `...:shipmentNo:806` id is a prefix of `...:shipmentNo:8061`), so a
    // LIKE would pull another shipment's change docs into this merge.
    //
    // Both the `shipmentsnapshot_id` join and the `snapshot_change_id` lines
    // lookup are foreign keys that 0382 left unindexed; migration 0484 indexes
    // them, without which this read scans both change tables in full.
    //
    // The otp row carries no `password`: it is the plaintext secret handed to
    // the recipient out of band, and these rows are rebuilt into an entity that
    // can go to the driver's browser. `verified` carries what the read path
    // actually needs — whether the code was entered and matched. See
    // pg-rows-to-shipment-docs.
    const query = `
      WITH snapshot_rows AS (
        SELECT
          ss.couchdb_id,
          ss.status,
          ${isoUtc('ss.created_at')} AS created_at,
          ${isoUtc('ss.updated_at')} AS updated_at,
          ss.created_by,
          ss.parent_doc_id,
          ss.order_id,
          ss.raw_order_id,
          ss.program_id,
          ss.funder_id,
          ss.route_id,
          ss.vendor_id,
          ss.is_automatic_return,
          ss.is_gps_authenticated,
          ss.gps_validation_fail_comment,
          ss.gps_validation,
          (
            SELECT jsonb_object_agg(
              ssld.batch,
              jsonb_build_object('quantity', ssld.amount)
            )
            FROM avocado.data_shipmentsnapshotline ssl
            JOIN avocado.data_shipmentsnapshotline_details ssld ON ssld.shipmentsnapshot_line = ssl.id
            WHERE ssl.shipment_snapshot_id = ss.id
          ) AS counts
        FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
        WHERE ss.shipment_id = $1
      ),
      change_rows AS (
        SELECT
          sc.couchdb_id,
          sc.snapshot_couchdb_id,
          sc.device_id,
          sc.version,
          sc.clock,
          ${isoUtc('sc.created_at')} AS created_at,
          ${isoUtc('sc.updated_at')} AS updated_at,
          sc.created_by,
          (
            SELECT jsonb_object_agg(
              scl.batch_id,
              jsonb_strip_nulls(jsonb_build_object(
                'quantity', scl.quantity,
                'checked', scl.checked,
                'timestamp', scl.timestamp,
                'paymentType', scl.payment_type,
                'removed', scl.removed
              ))
            )
            FROM avocado.data_shipmentsnapshot_change_lines scl
            WHERE scl.snapshot_change_id = sc.id
          ) AS changes
        FROM ${DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME} sc
        JOIN ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss ON ss.id = sc.shipmentsnapshot_id
        WHERE ss.shipment_id = $1
      ),
      comment_rows AS (
        SELECT
          c.couchdb_id,
          ${isoUtc('c.created_at')} AS created_at,
          c.created_by,
          c.comment,
          c.signature,
          c.name,
          c.adjustment_reasons
        FROM ${DATA_SHIPMENT_COMMENT_TABLE_NAME} c
        WHERE c.shipment_id = $1
      ),
      collector_rows AS (
        SELECT
          col.couchdb_id,
          ${isoUtc('col.created_at')} AS created_at,
          col.created_by,
          col.collector_type,
          col.name,
          col.phone_number,
          col.signature,
          col.comment
        FROM ${DATA_SHIPMENT_COLLECTOR_TABLE_NAME} col
        WHERE col.shipment_id = $1
      ),
      otp_rows AS (
        SELECT so.shipment_id, so.otp_key, so.user_fail_reason, so.verified
        FROM avocado.shipment_otp so
        WHERE so.shipment_id = $1
      )
      SELECT 'snapshot' AS kind, to_jsonb(s) AS doc, s.created_at AS sort_key
      FROM snapshot_rows s
      UNION ALL
      SELECT 'change' AS kind, to_jsonb(c) AS doc, c.created_at AS sort_key
      FROM change_rows c
      UNION ALL
      SELECT 'comment' AS kind, to_jsonb(m) AS doc, m.created_at AS sort_key
      FROM comment_rows m
      UNION ALL
      SELECT 'collector' AS kind, to_jsonb(col) AS doc, col.created_at AS sort_key
      FROM collector_rows col
      UNION ALL
      SELECT 'otp' AS kind, to_jsonb(o) AS doc, NULL::text AS sort_key
      FROM otp_rows o
      ORDER BY kind, sort_key ASC
    `

    const {rows} = await this.pgConnection.query(query, [shipmentId])

    // Ordering by `kind` groups each set's rows contiguously and `sort_key`
    // (the fixed-width UTC ISO string) orders within it, so collecting in row
    // order reproduces the `created_at ASC` each separate query returned.
    const docsByKind = rows.reduce((acc, {kind, doc}) => {
      acc[kind].push(doc)
      return acc
    }, {snapshot: [], change: [], comment: [], collector: [], otp: []})

    return {
      snapshots: docsByKind.snapshot,
      changes: docsByKind.change,
      comments: docsByKind.comment,
      collectors: docsByKind.collector,
      otp: docsByKind.otp[0] || null
    }
  }
}

module.exports = ShipmentSnapshotAdapter
